import React from 'react';
import { motion } from 'framer-motion';
import { Phone, MapPin, Zap } from 'lucide-react';
import { PHONES } from '../content';

const STEPS = [
  {
    num: '01',
    icon: Phone,
    title: 'Ara',
    desc: 'Tek tuşla bağlan. 7/24 görevli operatörümüz sizi karşılayacak.',
    micro: 'Ortalama bekleme: 0 saniye',
    color: 'var(--red)',
    bg: 'rgba(229,51,51,.1)',
    border: 'rgba(229,51,51,.25)',
  },
  {
    num: '02',
    icon: MapPin,
    title: 'Konumunu Paylaş',
    desc: 'GPS konumunuzu gönderin veya adres tarif edin. Ekibimiz yola çıkıyor.',
    micro: 'WhatsApp ile de gönderebilirsiniz',
    color: 'var(--blue)',
    bg: 'var(--blue-dim)',
    border: 'var(--blue-border)',
  },
  {
    num: '03',
    icon: Zap,
    title: 'Biz Gelelim',
    desc: 'En yakın ekibimiz ortalama 30 dk\'da yanınızda. Tamir yerinde, aracınızın başında.',
    micro: 'Servise götürme yok',
    color: 'var(--green)',
    bg: 'var(--green-dim)',
    border: 'var(--green-border)',
  },
];

export default function HowItWorks() {
  return (
    <section
      data-testid="how-it-works"
      className="section"
      style={{
        background: 'var(--surface)',
        borderTop: '1px solid var(--border)',
        borderBottom: '1px solid var(--border)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div className="wrap">
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '3.5rem' }}>
          <div className="pill" style={{ marginBottom: '1rem' }}>3 Adım</div>
          <h2 style={{
            fontFamily: "'Space Grotesk',sans-serif", fontWeight: 800,
            fontSize: 'clamp(1.6rem, 4vw, 2.75rem)',
            color: 'var(--text)', lineHeight: 1.08,
            letterSpacing: '-.035em', marginBottom: '.625rem',
          }}>
            Bu Kadar <span style={{ color: 'var(--blue)' }}>Basit.</span>
          </h2>
          <p style={{ fontSize: '.9rem', color: 'var(--text2)', maxWidth: 420, margin: '0 auto' }}>
            3 adımda yardım alın. Karmaşık süreç yok.
          </p>
        </div>

        {/* Steps */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '1.5rem',
          maxWidth: 900,
          margin: '0 auto',
        }} className="steps-grid">
          {STEPS.map((step, i) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={step.num}
                data-testid={`hiw-step-${i}`}
                initial={{ opacity: 0, y: 25 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: .5, delay: i * .12 }}
                style={{
                  background: 'var(--card)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--rl)',
                  padding: '2.25rem 1.75rem',
                  textAlign: 'center',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'border-color .25s, transform .2s',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.borderColor = step.border;
                  e.currentTarget.style.transform = 'translateY(-3px)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.borderColor = 'var(--border)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {/* Step number watermark */}
                <div style={{
                  position: 'absolute', top: '-8px', right: '12px',
                  fontFamily: "'Space Grotesk',sans-serif", fontWeight: 800,
                  fontSize: '5rem', color: 'var(--border)', opacity: .3,
                  lineHeight: 1, pointerEvents: 'none',
                }}>{step.num}</div>

                {/* Icon */}
                <div style={{
                  width: 56, height: 56, borderRadius: 14,
                  background: step.bg, border: `1px solid ${step.border}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: step.color, margin: '0 auto 1rem',
                  position: 'relative', zIndex: 1,
                }}>
                  <Icon size={24} strokeWidth={1.75} />
                </div>

                <h3 style={{
                  fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700,
                  fontSize: '1.15rem', color: 'var(--text)', marginBottom: '.5rem',
                  position: 'relative', zIndex: 1,
                }}>{step.title}</h3>

                <p style={{
                  fontSize: '.85rem', color: 'var(--text2)', lineHeight: 1.65,
                  marginBottom: '.75rem', position: 'relative', zIndex: 1,
                }}>{step.desc}</p>

                {/* Microcopy */}
                <span style={{
                  fontSize: '.7rem', color: step.color, fontWeight: 600,
                  letterSpacing: '.03em', position: 'relative', zIndex: 1,
                }}>{step.micro}</span>
              </motion.div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
          <a
            href={PHONES.main_tel}
            data-testid="hiw-cta"
            className="btn btn-primary btn-xl ring num"
            style={{ gap: '.625rem' }}
          >
            <Phone size={17} /> Bu Kadar Basit — Şimdi Ara
          </a>
          <div style={{
            marginTop: '.875rem', fontSize: '.75rem', color: 'var(--text2)',
          }}>
            Ortalama <strong style={{ color: 'var(--text)' }}>30 dakikada</strong> yanınızdayız
          </div>
        </div>
      </div>

      <style>{`
        @media(max-width:768px){
          .steps-grid{grid-template-columns:1fr!important}
        }
      `}</style>
    </section>
  );
}
